export type DeployedVersionNotice = {
  dismiss: () => void;
};

export type WatchDeployedVersionOptions = {
  onNewVersion: (notice: DeployedVersionNotice) => void;
  versionUrl?: string;
  checkIntervalMs?: number;
  minimumTabAgeMs?: number;
  reminderDelayMs?: number;
  reloadOnStaleChunk?: boolean;
  reloadPage?: () => void;
};

/**
 * Watches the deployed `hash.txt` and calls `onNewVersion` once it differs
 * from the one this tab started with, but not before the tab is
 * `minimumTabAgeMs` old (a day by default): with several deploys a day, a
 * fresher tab is recent enough and the notice would only nag. Reloading is
 * left to the user: an automatic reload could cut off a request in flight or
 * drop state kept above the router.
 *
 * Once the user closes the notice without reloading, the UI calls
 * `notice.dismiss()` and `onNewVersion` runs again at the first check
 * `reminderDelayMs` later (four hours by default). Elapsed time is read from
 * the clock, so hours a laptop spent asleep count.
 *
 * Unless `reloadOnStaleChunk` is `false`, a lazy chunk that fails to load
 * (`vite:preloadError`) reloads the page, since that page is already broken.
 *
 * Returns a function that removes every timer and listener.
 */
export function watchDeployedVersion(
  options: WatchDeployedVersionOptions,
): () => void {
  const {
    onNewVersion,
    versionUrl = "/hash.txt",
    checkIntervalMs = FIVE_MINUTES_MS,
    minimumTabAgeMs = ONE_DAY_MS,
    reminderDelayMs = FOUR_HOURS_MS,
    reloadOnStaleChunk = true,
    reloadPage = () => window.location.reload(),
  } = options;

  const startedAt = Date.now();
  let bootVersion: string | undefined;
  let isOutdated = false;
  let isNoticeShown = false;
  let remindAt: number | undefined;
  let lastCheckAt = 0;
  let intervalId: ReturnType<typeof setInterval> | undefined;

  const checkDeployedVersion = async (): Promise<void> => {
    const isTabTooRecent = Date.now() - startedAt < minimumTabAgeMs;
    if (bootVersion !== undefined && isTabTooRecent) {
      return;
    }
    lastCheckAt = Date.now();
    const deployed = await readDeployedVersion(versionUrl);
    if (deployed.status !== "served" || isOutdated) {
      return;
    }
    if (bootVersion === undefined) {
      bootVersion = deployed.version;
      return;
    }
    if (deployed.version === bootVersion) {
      return;
    }
    isOutdated = true;
    showNotice();
  };

  const showNotice = (): void => {
    isNoticeShown = true;
    remindAt = undefined;
    onNewVersion({
      dismiss: () => {
        if (!isNoticeShown) {
          return;
        }
        isNoticeShown = false;
        remindAt = Date.now() + reminderDelayMs;
      },
    });
  };

  const remindIfDue = (): void => {
    if (remindAt !== undefined && Date.now() >= remindAt) {
      showNotice();
    }
  };

  const checkOrRemind = (): void => {
    if (isOutdated) {
      remindIfDue();
      return;
    }
    void checkDeployedVersion();
  };

  const checkWhenVisibleAgain = (): void => {
    if (document.visibilityState !== "visible") {
      return;
    }
    const isThrottled = Date.now() - lastCheckAt < ONE_MINUTE_MS;
    if (isOutdated || !isThrottled) {
      checkOrRemind();
    }
  };

  const reloadForStaleChunk = (event: Event): void => {
    if (wasReloadedForStaleChunkRecently()) {
      return;
    }
    rememberStaleChunkReload();
    event.preventDefault();
    reloadPage();
  };

  if (reloadOnStaleChunk) {
    window.addEventListener("vite:preloadError", reloadForStaleChunk);
  }

  let isStopped = false;
  void readDeployedVersion(versionUrl).then((deployed) => {
    if (isStopped || deployed.status === "not-served") {
      return;
    }
    if (deployed.status === "served") {
      bootVersion = deployed.version;
    }
    lastCheckAt = Date.now();
    intervalId = setInterval(checkOrRemind, checkIntervalMs);
    document.addEventListener("visibilitychange", checkWhenVisibleAgain);
  });

  return function stopWatchingDeployedVersion(): void {
    isStopped = true;
    clearInterval(intervalId);
    document.removeEventListener("visibilitychange", checkWhenVisibleAgain);
    window.removeEventListener("vite:preloadError", reloadForStaleChunk);
  };
}

const ONE_MINUTE_MS = 60_000;
const FIVE_MINUTES_MS = 5 * ONE_MINUTE_MS;
const FOUR_HOURS_MS = 4 * 60 * ONE_MINUTE_MS;
const ONE_DAY_MS = 24 * 60 * ONE_MINUTE_MS;
const STALE_CHUNK_RELOAD_GUARD_MS = 10_000;
const STALE_CHUNK_RELOAD_STORAGE_KEY = "front-version-reload:stale-chunk-at";
const COMMIT_SHA_PATTERN = /^[0-9a-f]{7,40}$/;

type DeployedVersion =
  | { status: "served"; version: string }
  | { status: "not-served" }
  | { status: "unreachable" };

async function readDeployedVersion(
  versionUrl: string,
): Promise<DeployedVersion> {
  try {
    // hash.txt is served with max-age=900: bypass the browser cache and, with
    // the query string, any intermediate cache keyed on the full URL.
    const response = await fetch(`${versionUrl}?t=${Date.now()}`, {
      cache: "no-store",
    });
    if (!response.ok) {
      return { status: "unreachable" };
    }
    const body = (await response.text()).trim();
    // A host without hash.txt answers with the SPA's index.html and a 200.
    return COMMIT_SHA_PATTERN.test(body)
      ? { status: "served", version: body }
      : { status: "not-served" };
  } catch {
    return { status: "unreachable" };
  }
}

// A chunk that is still missing after a reload would otherwise reload forever.
function wasReloadedForStaleChunkRecently(): boolean {
  try {
    const reloadedAt = Number(
      window.sessionStorage.getItem(STALE_CHUNK_RELOAD_STORAGE_KEY),
    );
    return Date.now() - reloadedAt < STALE_CHUNK_RELOAD_GUARD_MS;
  } catch {
    return false;
  }
}

function rememberStaleChunkReload(): void {
  try {
    window.sessionStorage.setItem(
      STALE_CHUNK_RELOAD_STORAGE_KEY,
      String(Date.now()),
    );
  } catch {
    // sessionStorage can be unavailable (private mode quota); reload anyway.
  }
}
